
import { useState, useRef } from 'react';

interface HistoryRecord {
  id: string;
  date: string;
  type: string;
  period: string;
  status: '已报送' | '待报送' | '逾期';
  operator: string;
  // 快照数据
  snapshot: {
    varieties: Array<{
      variety: string;
      grainNature: string;
      openingStock: number;
      closingStock: number;
      purchase: number;
      sales: number;
      purchasePrice: string;
      salesPrice: string;
      purchaseSource: string;
      salesTarget: string;
      storageLocation: string;
      storageMethod: string;
      qualityGrade: string;
      moistureContent: string;
      impurityRate: string;
      unsoundKernel: string;
      storageQuality: string;
      foodSafety: string;
      settlementMethod: string;
      purchaseAmount: number;
      salesAmount: number;
      costAmount: number;
      taxAmount: number;
      subsidyAmount: number;
    }>;
    department: string;
    totalVarieties: number;
  };
}

interface Props {
  record: HistoryRecord;
  onClose: () => void;
}

export default function HistoryDetailModal({ record, onClose }: Props) {
  const [exporting, setExporting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { snapshot } = record;
  const varieties = snapshot.varieties;

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');

      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 20;
      }

      pdf.save(`报送文件_${record.period}_${record.date}.pdf`);
    } catch {
      // fallback: print
      window.print();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[1000px] max-h-[92vh] overflow-hidden relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="sticky top-0 bg-white z-10 px-8 pt-6 pb-4 border-b border-gray-200 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#0c7362]/10 rounded-xl flex items-center justify-center">
              <i className="ri-history-line text-xl text-[#0c7362]"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                历史报送详情 — {record.period}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {record.type} · 报送日期：{record.date} · 操作人：{record.operator}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="px-4 py-2 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {exporting ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin"></i>导出中...
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-2-line mr-1.5"></i>导出PDF
                </>
              )}
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line mr-1.5"></i>打印
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* 文件内容 */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div
            ref={contentRef}
            className="bg-white border border-gray-300 rounded-lg shadow-sm mx-auto max-w-[900px]"
            style={{ fontFamily: 'SimSun, serif' }}
          >
            {/* 状态标记 */}
            <div className="px-10 pt-4 flex justify-end">
              <span
                className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold ${
                  record.status === '已报送'
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : record.status === '待报送'
                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                    : 'bg-rose-100 text-rose-700 border border-rose-300'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${
                    record.status === '已报送'
                      ? 'bg-emerald-500'
                      : record.status === '待报送'
                      ? 'bg-amber-500'
                      : 'bg-rose-500'
                  }`}
                ></div>
                {record.status}
              </span>
            </div>

            {/* 文件标题 */}
            <div className="border-b-2 border-gray-800 px-10 pt-4 pb-6 text-center">
              <div className="text-xs text-gray-500 mb-3 tracking-widest">密级：内部</div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">
                粮食经营台账数据报送表
              </h1>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-4">
                <span>
                  报送类型：<strong className="text-gray-800">{record.type}</strong>
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  报送周期：<strong className="text-gray-800">{record.period}</strong>
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  报送部门：<strong className="text-gray-800">{snapshot.department}</strong>
                </span>
              </div>
            </div>

            <div className="px-10 py-6 space-y-6">
              {/* 一、经营主体基本信息 */}
              <div>
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                    一
                  </span>
                  经营主体基本信息
                </h3>
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    <tr>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 w-[130px] font-medium">
                        企业名称
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">
                        丰收粮食贸易有限公司
                      </td>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 w-[130px] font-medium">
                        统一社会信用代码
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">
                        91410100MA47XXXXXX
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">
                        法定代表人
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">张建国</td>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">
                        联系电话
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">
                        0371-6688XXXX
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">
                        经营地址
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800" colSpan={3}>
                        河南省郑州市中原区粮食产业园A栋
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">
                        经营品种数
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">
                        {snapshot.totalVarieties} 种
                      </td>
                      <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">
                        报送日期
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-gray-800">
                        {record.date}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 二、购进销售储存汇总 */}
              <div>
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                    二
                  </span>
                  粮食购进、销售、储存汇总表
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>
                          粮食品种
                        </th>
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>
                          粮食性质
                        </th>
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={2}>
                          库存（吨）
                        </th>
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={3}>
                          购进
                        </th>
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={3}>
                          销售
                        </th>
                        <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>
                          储存地点
                        </th>
                      </tr>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">期初</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">期末</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">数量(吨)</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">单价(元/吨)</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">来源</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">数量(吨)</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">单价(元/吨)</th>
                        <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">去向</th>
                      </tr>
                    </thead>
                    <tbody>
                      {varieties.map((item, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <td className="border border-gray-400 px-2 py-2 text-center font-medium text-gray-800">
                            {item.variety}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">
                            {item.grainNature}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">
                            {item.openingStock.toLocaleString()}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">
                            {item.closingStock.toLocaleString()}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-emerald-700 font-medium">
                            {item.purchase.toLocaleString()}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">
                            {item.purchasePrice}
                          </td>
                          <td
                            className="border border-gray-400 px-2 py-2 text-gray-600 max-w-[100px] truncate"
                            title={item.purchaseSource}
                          >
                            {item.purchaseSource}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-rose-700 font-medium">
                            {item.sales.toLocaleString()}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">
                            {item.salesPrice}
                          </td>
                          <td
                            className="border border-gray-400 px-2 py-2 text-gray-600 max-w-[100px] truncate"
                            title={item.salesTarget}
                          >
                            {item.salesTarget || '-'}
                          </td>
                          <td className="border border-gray-400 px-2 py-2 text-gray-600 text-center">
                            {item.storageLocation}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold">
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-800">合计</td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                        <td className="border border-gray-400 px-2 py-2 text-right text-gray-800">
                          {varieties.reduce((s, i) => s + i.openingStock, 0).toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-2 py-2 text-right text-gray-800">
                          {varieties.reduce((s, i) => s + i.closingStock, 0).toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-2 py-2 text-right text-emerald-700">
                          {varieties.reduce((s, i) => s + i.purchase, 0).toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                        <td className="border border-gray-400 px-2 py-2 text-right text-rose-700">
                          {varieties.reduce((s, i) => s + i.sales, 0).toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                        <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* 三、质量指标 */}
              <div>
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                    三
                  </span>
                  粮食质量指标
                </h3>
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">粮食品种</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">质量等级</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">水分含量</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">杂质率</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">不完善粒</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">储存品质</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">食品安全</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varieties.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="border border-gray-400 px-3 py-2 text-center font-medium text-gray-800">
                          {item.variety}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.qualityGrade}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.moistureContent}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.impurityRate}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.unsoundKernel}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.storageQuality}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.foodSafety}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 四、财务信息 */}
              <div>
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                    四
                  </span>
                  粮食经营财务信息
                </h3>
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">粮食品种</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">收购金额(元)</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">营业收入(元)</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">营业成本(元)</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">结算方式</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">应缴税款(元)</th>
                      <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">政府补贴(元)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varieties.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="border border-gray-400 px-3 py-2 text-center font-medium text-gray-800">
                          {item.variety}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-right text-gray-700">
                          {item.purchaseAmount.toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-right text-emerald-700 font-medium">
                          {item.salesAmount.toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-right text-gray-700">
                          {item.costAmount.toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">
                          {item.settlementMethod}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-right text-amber-700">
                          {item.taxAmount.toLocaleString()}
                        </td>
                        <td className="border border-gray-400 px-3 py-2 text-right text-[#0c7362]">
                          {item.subsidyAmount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold">
                      <td className="border border-gray-400 px-3 py-2 text-center text-gray-800">合计</td>
                      <td className="border border-gray-400 px-3 py-2 text-right text-gray-800">
                        {varieties.reduce((s, i) => s + i.purchaseAmount, 0).toLocaleString()}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-right text-emerald-700">
                        {varieties.reduce((s, i) => s + i.salesAmount, 0).toLocaleString()}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-right text-gray-800">
                        {varieties.reduce((s, i) => s + i.costAmount, 0).toLocaleString()}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-center text-gray-600">—</td>
                      <td className="border border-gray-400 px-3 py-2 text-right text-amber-700">
                        {varieties.reduce((s, i) => s + i.taxAmount, 0).toLocaleString()}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-right text-[#0c7362]">
                        {varieties.reduce((s, i) => s + i.subsidyAmount, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* 保密声明 */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                <i className="ri-shield-keyhole-line mr-1"></i>
                <strong>保密声明：</strong>
                本报送数据涉及商业秘密和个人隐私，严格按照《中华人民共和国保守国家秘密法》及相关规定执行。报送的基本数据和有关情况须与生产经营实际相符，与粮食经营台账相符。未经授权，不得向第三方披露。
              </div>

              {/* 签章区域 */}
              <div className="border-t-2 border-gray-800 pt-6 mt-6">
                <div className="grid grid-cols-3 gap-8 text-sm text-gray-700">
                  <div>
                    <div className="font-medium text-gray-800 mb-6">填报人（签字）：</div>
                    <div className="border-b border-gray-400 pb-1 mb-4"></div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 mb-6">企业负责人（签字）：</div>
                    <div className="border-b border-gray-400 pb-1 mb-4"></div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 mb-6">（企业公章）</div>
                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-400">公章位置</span>
                    </div>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-600 mt-6">
                  报送日期：{record.date}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="sticky bottom-0 bg-white px-8 py-4 border-t border-gray-200 rounded-b-2xl flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-500">
            <i className="ri-information-line mr-1.5 text-[#0c7362]"></i>
            共 {varieties.length} 个品种 · {record.period} · {record.type}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              关闭
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="px-8 py-2.5 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {exporting ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>导出中...
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-2-line mr-2"></i>导出PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
